import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner
} from 'reactstrap';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase, { API_PROXY_TARGET } from '../../apiConfig';
import { getAuthHeaders } from '../../helpers/utils';

const BASE_ENDPOINT = `${apiBase}/p-admin/pasport.php`;
const GROUPS_ENDPOINT = `${BASE_ENDPOINT}?action=groups`;
const GROUP_ENDPOINT = `${BASE_ENDPOINT}?action=group`;
const STUDENT_ENDPOINT = `${BASE_ENDPOINT}?action=student`;
const SAVE_ENDPOINT = `${BASE_ENDPOINT}?action=save`;
const BACKEND_ROOT = API_PROXY_TARGET.replace(/\/api\/?$/i, '');
const DEFAULT_PERMISSIONS = { view: false, manage: false };
const DEFAULT_TESTS = {
  temperament: 'Qiymatlanmagan',
  kommunikativ: 'Qiymatlanmagan',
  motivatsiya: 'Qiymatlanmagan',
  teylor: 'Qiymatlanmagan',
  nevratsiya: 'Qiymatlanmagan',
  dipressiya: 'Qiymatlanmagan',
  suitsit: 'Qiymatlanmagan',
  kasb: 'Qiymatlanmagan'
};
const EMPTY_GROUPS_DATA = {
  permissions: DEFAULT_PERMISSIONS,
  stats: {
    active_students: 0,
    faculty_count: 0,
    group_count: 0,
    completed_count: 0,
    pending_count: 0
  },
  faculties: []
};
const EMPTY_GROUP_DATA = {
  permissions: DEFAULT_PERMISSIONS,
  group: null,
  stats: {
    total_students: 0,
    completed_count: 0,
    pending_count: 0
  },
  students: []
};
const EMPTY_STUDENT_DATA = {
  permissions: DEFAULT_PERMISSIONS,
  student: null,
  tests: DEFAULT_TESTS
};
const PASSPORT_PAYLOAD_FIELDS = [
  'talaba_id',
  'oila',
  'bitirgan_nomi',
  'ota_ona_manzil',
  'otasi_fio',
  'onasi_fio',
  'lavozim',
  'harbiy_qism',
  'yashash_manzil',
  'shakl',
  'xarakter',
  'rang',
  'salomatlik',
  'ozlashtirish',
  'munosabat',
  'kursdosh',
  'izoh'
];
const TEST_CARDS = [
  { key: 'temperament', label: 'Temperamenti', icon: 'chart-pie' },
  { key: 'kommunikativ', label: 'Kommunikativ', icon: 'comments' },
  { key: 'motivatsiya', label: 'Motivatsiya', icon: 'bullseye' },
  { key: 'teylor', label: 'Bezovtalanish', icon: 'exclamation-circle' },
  { key: 'nevratsiya', label: 'Nevrasteniya', icon: 'bolt' },
  { key: 'dipressiya', label: 'Depressiya', icon: 'moon' },
  { key: 'suitsit', label: 'Suitsid testi', icon: 'exclamation-triangle' },
  { key: 'kasb', label: 'Kasb testi', icon: 'briefcase' }
];
const SHAPE_OPTIONS = [
  { value: '1', label: '1-shakl', asset: '/student-assets/rasm/4.jpg' },
  { value: '2', label: '2-shakl', asset: '/student-assets/rasm/3.jpg' },
  { value: '3', label: '3-shakl', asset: '/student-assets/rasm/aylana.jpg' },
  { value: '4', label: '4-shakl', asset: '/student-assets/rasm/z.jpg' },
  { value: '5', label: '5-shakl', asset: '/student-assets/rasm/tort.jpg' }
];
const OILA_OPTIONS = [
  { value: '0', label: '-- Tanlang --' },
  { value: '1', label: 'Uylanmagan / turmushga chiqmagan' },
  { value: '2', label: 'Uylangan / turmushga chiqqan' }
];
const XARAKTER_OPTIONS = [
  { value: '0', label: '-- Tanlang --' },
  { value: '1', label: "Og'ir" },
  { value: '2', label: "Sho'x" },
  { value: '3', label: 'Qiziqqon' },
  { value: '4', label: 'Quvnoq' },
  { value: '5', label: 'Qaysar' }
];

const readGroupId = search => {
  const params = new URLSearchParams(search || '');
  return (params.get('guruh_id') || '').trim();
};

const getFacultyKey = faculty => faculty.code || faculty.name;

const extractErrorMessage = (error, fallback) =>
  (error.response && error.response.data && error.response.data.message) || error.message || fallback;

const toBackendUrl = path => {
  if (!path) {
    return '';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path.startsWith('/')) {
    return `${BACKEND_ROOT}${path}`;
  }

  return `${BACKEND_ROOT}/${path.replace(/^\/+/, '')}`;
};

const buildPreviewPath = talabaId => `/psixologik-pasport/view?talaba_id=${encodeURIComponent(String(talabaId || ''))}`;

const completionMeta = completed =>
  completed
    ? {
        label: "To'ldirilgan",
        className: 'psi-passport-state psi-passport-state--success'
      }
    : {
        label: "To'ldirilmagan",
        className: 'psi-passport-state psi-passport-state--muted'
      };

const toFormState = student => ({
  talaba_id: student && student.talaba_id ? String(student.talaba_id) : '',
  full_name: (student && student.full_name) || '',
  faculty: (student && student.faculty) || '',
  group: (student && student.group) || '',
  birth_date: (student && student.birth_date) || '',
  address: (student && student.address) || '',
  image_url: (student && student.image_url) || '',
  oila: String((student && student.oila) || 0),
  bitirgan_nomi: (student && student.bitirgan_nomi) || '',
  ota_ona_manzil: (student && student.ota_ona_manzil) || '',
  otasi_fio: (student && student.otasi_fio) || '',
  onasi_fio: (student && student.onasi_fio) || '',
  lavozim: (student && student.lavozim) || '',
  harbiy_qism: (student && student.harbiy_qism) || '',
  yashash_manzil: (student && student.yashash_manzil) || '',
  shakl: String((student && student.shakl) || 0),
  xarakter: String((student && student.xarakter) || 0),
  rang: (student && student.rang) || '',
  salomatlik: (student && student.salomatlik) || '',
  ozlashtirish: (student && student.ozlashtirish) || '',
  munosabat: (student && student.munosabat) || '',
  kursdosh: (student && student.kursdosh) || '',
  izoh: (student && student.izoh) || ''
});

const PassportManagement = ({ history, location }) => {
  const [groupsData, setGroupsData] = useState(EMPTY_GROUPS_DATA);
  const [groupData, setGroupData] = useState(EMPTY_GROUP_DATA);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [banner, setBanner] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [completionFilter, setCompletionFilter] = useState('all');
  const [expandedFaculties, setExpandedFaculties] = useState({});
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailData, setDetailData] = useState(EMPTY_STUDENT_DATA);
  const [formState, setFormState] = useState(toFormState(null));
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const groupId = readGroupId(location.search);

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  const changeGroup = useCallback(
    nextGroupId => {
      const search = nextGroupId ? `?guruh_id=${encodeURIComponent(nextGroupId)}` : '';
      history.push({
        pathname: '/psixologik-pasport',
        search
      });
    },
    [history]
  );

  const loadGroups = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoadingGroups(true);
      }

      try {
        const response = await axios.get(GROUPS_ENDPOINT, requestConfig);

        if (response.data && response.data.success) {
          setGroupsData(response.data.data || EMPTY_GROUPS_DATA);
          return;
        }

        throw new Error((response.data && response.data.message) || 'Pasport guruhlari yuklanmadi.');
      } catch (error) {
        setBanner({
          color: 'danger',
          text: extractErrorMessage(error, 'Pasport guruhlarini yuklashda xatolik yuz berdi.')
        });
      } finally {
        if (!silent) {
          setLoadingGroups(false);
        }
      }
    },
    [requestConfig]
  );

  const loadGroup = useCallback(
    async selectedGroupId => {
      if (!selectedGroupId) {
        setGroupData(EMPTY_GROUP_DATA);
        setLoadingGroup(false);
        setNotFoundMessage('');
        return;
      }

      setLoadingGroup(true);
      setNotFoundMessage('');

      try {
        const response = await axios.get(GROUP_ENDPOINT, {
          ...requestConfig,
          params: {
            guruh_id: selectedGroupId
          }
        });

        if (response.data && response.data.success) {
          setGroupData(response.data.data || EMPTY_GROUP_DATA);
          return;
        }

        throw new Error((response.data && response.data.message) || 'Guruh ma`lumotlari yuklanmadi.');
      } catch (error) {
        const status = error.response && error.response.status;
        const message = extractErrorMessage(error, 'Guruh ma`lumotlarini yuklashda xatolik yuz berdi.');

        setGroupData(EMPTY_GROUP_DATA);

        if (status === 404) {
          setNotFoundMessage(message);
          return;
        }

        setBanner({
          color: 'danger',
          text: message
        });
      } finally {
        setLoadingGroup(false);
      }
    },
    [requestConfig]
  );

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadGroup(groupId);
  }, [groupId, loadGroup]);

  const filteredFaculties = useMemo(() => {
    const search = groupSearch.trim().toLowerCase();

    return (groupsData.faculties || [])
      .map(faculty => {
        const groups = (faculty.groups || []).filter(group => {
          if (!search) {
            return true;
          }

          return (
            group.name.toLowerCase().includes(search) ||
            (group.value || '').toLowerCase().includes(search)
          );
        });

        return {
          ...faculty,
          groups
        };
      })
      .filter(faculty => faculty.groups.length > 0);
  }, [groupSearch, groupsData.faculties]);

  useEffect(() => {
    if (!filteredFaculties.length) {
      return;
    }

    setExpandedFaculties(current => {
      const next = { ...current };

      if (groupId) {
        const selectedFaculty = filteredFaculties.find(faculty =>
          (faculty.groups || []).some(group => group.value === groupId)
        );

        if (selectedFaculty) {
          next[getFacultyKey(selectedFaculty)] = true;
          return next;
        }
      }

      if (!Object.values(next).some(Boolean)) {
        next[getFacultyKey(filteredFaculties[0])] = true;
      }

      return next;
    });
  }, [filteredFaculties, groupId]);

  const toggleFaculty = facultyKey => {
    setExpandedFaculties(current => ({
      ...current,
      [facultyKey]: !current[facultyKey]
    }));
  };

  const filteredStudents = useMemo(() => {
    const search = studentSearch.trim().toLowerCase();

    return (groupData.students || []).filter(student => {
      const matchesSearch =
        !search ||
        student.full_name.toLowerCase().includes(search) ||
        String(student.talaba_id).includes(search) ||
        (student.passport || '').toLowerCase().includes(search) ||
        (student.address || '').toLowerCase().includes(search);

      if (!matchesSearch) {
        return false;
      }

      if (completionFilter === 'completed') {
        return Boolean(student.profile_complete);
      }

      if (completionFilter === 'pending') {
        return !student.profile_complete;
      }

      return true;
    });
  }, [completionFilter, groupData.students, studentSearch]);

  const permissions = groupsData.permissions || DEFAULT_PERMISSIONS;

  const openStudent = useCallback(
    async talabaId => {
      setDetailModalOpen(true);
      setDetailLoading(true);
      setImageFile(null);
      setBanner(null);

      try {
        const response = await axios.get(STUDENT_ENDPOINT, {
          ...requestConfig,
          params: {
            talaba_id: talabaId
          }
        });

        if (response.data && response.data.success) {
          const payload = response.data.data || EMPTY_STUDENT_DATA;
          const nextStudent = payload.student || null;

          setDetailData({
            permissions: payload.permissions || DEFAULT_PERMISSIONS,
            student: nextStudent,
            tests: payload.tests || DEFAULT_TESTS
          });
          setFormState(toFormState(nextStudent));
          setImagePreview(nextStudent ? toBackendUrl(nextStudent.image_url) : '');
          return;
        }

        throw new Error((response.data && response.data.message) || 'Talaba pasporti yuklanmadi.');
      } catch (error) {
        const message = extractErrorMessage(error, 'Talaba pasportini yuklashda xatolik yuz berdi.');
        toast.error(message);
        setDetailModalOpen(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [requestConfig]
  );

  const closeModal = () => {
    if (detailSaving) {
      return;
    }

    setDetailModalOpen(false);
    setDetailLoading(false);
    setDetailData(EMPTY_STUDENT_DATA);
    setFormState(toFormState(null));
    setImageFile(null);
    setImagePreview('');
  };

  const handleFieldChange = ({ target }) => {
    const { name, value } = target;
    setFormState(current => ({
      ...current,
      [name]: value
    }));
  };

  const handleImageChange = event => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      setImageFile(null);
      setImagePreview(toBackendUrl(formState.image_url || ''));
      return;
    }

    if (file.size > 1048576) {
      event.target.value = '';
      toast.error('Rasm hajmi 1 MB dan oshmasligi kerak.');
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onload = loadEvent => {
      setImagePreview(loadEvent.target && loadEvent.target.result ? loadEvent.target.result : '');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!permissions.manage) {
      toast.error('Sizda tahrirlash uchun ruxsat yo`q.');
      return;
    }

    if (!formState.talaba_id) {
      return;
    }

    setDetailSaving(true);
    setBanner(null);

    try {
      const payload = new FormData();

      PASSPORT_PAYLOAD_FIELDS.forEach(field => {
        payload.append(field, formState[field] == null ? '' : formState[field]);
      });

      if (imageFile) {
        payload.append('rasm', imageFile);
      }

      const response = await axios.post(SAVE_ENDPOINT, payload, requestConfig);

      if (response.data && response.data.success) {
        const responseData = response.data.data || {};
        const nextStudent = responseData.student || detailData.student;
        const nextTests = responseData.tests || detailData.tests;
        const nextGroupData = responseData.group || null;

        setDetailData({
          permissions: responseData.permissions || detailData.permissions || DEFAULT_PERMISSIONS,
          student: nextStudent,
          tests: nextTests
        });
        setFormState(toFormState(nextStudent));
        setImageFile(null);
        setImagePreview(nextStudent ? toBackendUrl(nextStudent.image_url) : '');

        if (nextGroupData) {
          setGroupData(nextGroupData);
        }

        await loadGroups(true);

        setBanner({
          color: 'success',
          text: response.data.message || 'Psixologik pasport saqlandi.'
        });
        toast.success(response.data.message || 'Psixologik pasport saqlandi.');
        return;
      }

      throw new Error((response.data && response.data.message) || 'Saqlashda xatolik yuz berdi.');
    } catch (error) {
      const message = extractErrorMessage(error, 'Psixologik pasportni saqlashda xatolik yuz berdi.');
      setBanner({
        color: 'danger',
        text: message
      });
      toast.error(message);
    } finally {
      setDetailSaving(false);
    }
  };

  const statCards = groupId
    ? [
        {
          title: 'Jami talabalar',
          value: groupData.stats.total_students,
          tone: 'primary',
          icon: 'users',
          note: 'tanlangan guruh'
        },
        {
          title: "To'ldirilgan",
          value: groupData.stats.completed_count,
          tone: 'success',
          icon: 'id-card',
          note: 'tayyor pasport'
        },
        {
          title: "To'ldirilmagan",
          value: groupData.stats.pending_count,
          tone: 'warning',
          icon: 'exclamation-circle',
          note: 'kutayotganlar'
        }
      ]
    : [
        {
          title: 'Faol talabalar',
          value: groupsData.stats.active_students,
          tone: 'primary',
          icon: 'users',
          note: 'pasport modulida'
        },
        {
          title: 'Fakultetlar',
          value: groupsData.stats.faculty_count,
          tone: 'info',
          icon: 'university',
          note: 'aktiv bo`limlar'
        },
        {
          title: 'Guruhlar',
          value: groupsData.stats.group_count,
          tone: 'secondary',
          icon: 'stream',
          note: 'boshqariladi'
        },
        {
          title: "To'ldirilgan",
          value: groupsData.stats.completed_count,
          tone: 'success',
          icon: 'check-circle',
          note: 'tayyor pasport'
        }
      ];

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'Talaba',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-passport-student">
            <div className="psi-passport-student__avatar">{row.initials}</div>
            <div className="psi-passport-student__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'birth_date',
        text: "Tug'ilgan sana",
        sort: true,
        classes: 'align-middle',
        formatter: value => value || "Ko'rsatilmagan"
      },
      {
        dataField: 'address',
        text: 'Manzil',
        sort: true,
        classes: 'align-middle',
        formatter: value => <div className="psi-passport-address">{value || "Ko'rsatilmagan"}</div>
      },
      {
        dataField: 'profile_complete',
        text: 'Holat',
        sort: true,
        classes: 'align-middle',
        formatter: value => {
          const meta = completionMeta(Boolean(value));

          return (
            <Badge pill className={meta.className}>
              {meta.label}
            </Badge>
          );
        }
      },
      {
        dataField: 'actions',
        text: 'Boshqaruv',
        classes: 'align-middle',
        headerStyle: () => ({ minWidth: '15rem' }),
        formatter: (_, row) => (
          <div className="psi-passport-actions">
            <Button
              color="light"
              size="sm"
              className="psi-passport-action-button psi-passport-action-button--primary"
              onClick={() => openStudent(row.talaba_id)}
            >
              <FontAwesomeIcon icon="edit" />
              <span>{permissions.manage ? 'Pasportni ochish' : "Ko'rish"}</span>
            </Button>
            <Button
              color="light"
              size="sm"
              className="psi-passport-action-button psi-passport-action-button--ghost"
              onClick={() => history.push(buildPreviewPath(row.talaba_id))}
            >
              <FontAwesomeIcon icon="file-pdf" />
              <span>Preview</span>
            </Button>
          </div>
        )
      }
    ],
    [history, openStudent, permissions.manage]
  );

  return (
    <>
      <div className="psi-review-heading mb-3">
        <div>
          <div className="psi-review-heading__nav">Boshqaruv / Psixologik pasport</div>
          <h3 className="mb-1">Psixologik pasport</h3>
          <p className="mb-0">Fakultet va guruh kesimida talabalar pasportini React panel orqali boshqaring.</p>
        </div>
        <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: '0.5rem' }}>
          <Badge pill className="psi-review-heading__badge">
            {permissions.manage ? 'Tahrirlash yoqilgan' : "Faqat ko'rish"}
          </Badge>
          {groupId ? (
            <Badge pill className="psi-review-heading__badge">
              {groupId}
            </Badge>
          ) : null}
        </div>
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <div className="psi-review-toolbar mb-3">
        <div className="psi-review-toolbar__copy">
          <h5 className="mb-1">{groupId ? `${groupId} guruhi` : 'Guruh tanlanmagan'}</h5>
          <p className="text-600 mb-0">
            {groupId
              ? "Tanlangan guruhdagi talabalarni ochib, psixologik pasportni to'ldirasiz yoki yangilaysiz."
              : 'Chap paneldan kerakli guruhni tanlang.'}
          </p>
        </div>

        <div className="psi-review-toolbar__controls">
          <Input
            value={groupSearch}
            onChange={({ target }) => setGroupSearch(target.value)}
            placeholder="Guruh qidirish"
            className="psi-review-search"
          />
          {groupId ? (
            <Button color="light" className="psi-review-switch-button" onClick={() => changeGroup('')}>
              Guruhni almashtirish
            </Button>
          ) : null}
        </div>
      </div>

      {notFoundMessage ? (
        <Alert color="warning" className="mb-3">
          {notFoundMessage}
        </Alert>
      ) : null}

      <Row className="mb-3">
        {statCards.map(card => (
          <Col md="6" xl key={card.title} className="mb-3">
            <Card className={`border-0 shadow-sm h-100 psi-review-stat psi-review-stat--${card.tone}`}>
              <CardBody>
                <div className="psi-review-stat__top">
                  <div className="psi-review-stat__icon">
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                  <div className="psi-review-stat__chip">{card.note}</div>
                </div>
                <div className="psi-review-stat__value">{card.value}</div>
                <div className="psi-review-stat__label">{card.title}</div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="mb-3 psi-review-layout">
        <Col xl="4" className="mb-3 mb-xl-0">
          <Card className="border-0 shadow-sm h-100 psi-review-sidebar-card">
            <CardBody className="psi-review-sidebar-card__body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Fakultetlar va guruhlar</h5>
                {loadingGroups ? <Spinner size="sm" color="primary" /> : <Badge color="light">{filteredFaculties.length} ta</Badge>}
              </div>

              <div className="psi-review-group-list">
                {loadingGroups ? (
                  <div className="text-center text-500 py-4">Guruhlar yuklanmoqda...</div>
                ) : filteredFaculties.length > 0 ? (
                  filteredFaculties.map(faculty => (
                    <div key={getFacultyKey(faculty)} className="psi-review-faculty">
                      <button
                        type="button"
                        className={`psi-review-faculty__header ${expandedFaculties[getFacultyKey(faculty)] ? 'is-open' : ''}`}
                        onClick={() => toggleFaculty(getFacultyKey(faculty))}
                      >
                        <div className="psi-review-faculty__copy">
                          <strong>{faculty.name}</strong>
                          <span>
                            {faculty.groups.length} ta guruh • {faculty.completed_count}/{faculty.active_count}
                          </span>
                        </div>
                        <span className="psi-review-faculty__toggle" aria-hidden="true">
                          {expandedFaculties[getFacultyKey(faculty)] ? '−' : '+'}
                        </span>
                      </button>
                      {expandedFaculties[getFacultyKey(faculty)] ? (
                        <div className="psi-review-faculty__groups">
                          {faculty.groups.map(group => (
                            <button
                              key={group.value}
                              type="button"
                              className={`psi-review-group-button ${groupId === group.value ? 'is-active' : ''}`}
                              onClick={() => changeGroup(group.value)}
                            >
                              <span>{group.name}</span>
                              <small>
                                {group.completed_count}/{group.active_count}
                              </small>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-500 py-4">Mos guruh topilmadi.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl="8" className="psi-review-main-col">
          {loadingGroup ? (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center">
                  <Spinner color="primary" className="mb-3" />
                  <div className="text-600">Guruh pasportlari yuklanmoqda...</div>
                </div>
              </CardBody>
            </Card>
          ) : groupId ? (
            <>
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: '1rem' }}>
                  <div>
                    <h5 className="mb-1">
                      {(groupData.group && groupData.group.name) || groupId}
                    </h5>
                    <p className="text-600 mb-0">
                      {groupData.group && groupData.group.faculty_name
                        ? `${groupData.group.faculty_name} fakulteti`
                        : 'Fakultet ko`rsatilmagan'}
                    </p>
                  </div>
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.65rem' }}>
                    <Input
                      value={studentSearch}
                      onChange={({ target }) => setStudentSearch(target.value)}
                      placeholder="Talaba qidirish"
                      className="psi-review-search"
                    />
                    <Input
                      type="select"
                      value={completionFilter}
                      onChange={({ target }) => setCompletionFilter(target.value)}
                      className="psi-review-filter"
                    >
                      <option value="all">Barchasi</option>
                      <option value="completed">To`ldirilgan</option>
                      <option value="pending">To`ldirilmagan</option>
                    </Input>
                    <TablePageSizeSelect
                      value={pageSize}
                      onChange={setPageSize}
                      className="psi-passport-page-size"
                    />
                  </div>
                </CardBody>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardBody>
                  <MedDataTable
                    data={filteredStudents}
                    columns={columns}
                    pageSize={pageSize}
                    rowClasses="psi-passport-row"
                    tableClasses="table table-hover fs--1 mb-0 psi-passport-table"
                    headerClasses="bg-200 text-900"
                    paginationResetKey={`${groupId}-${completionFilter}-${studentSearch}`}
                    defaultSorted={[
                      {
                        dataField: 'order',
                        order: 'asc'
                      }
                    ]}
                  />
                </CardBody>
              </Card>
            </>
          ) : (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center psi-passport-empty">
                  <div className="psi-passport-empty__icon">
                    <FontAwesomeIcon icon="id-card" />
                  </div>
                  <h5 className="mb-2">Psixologik pasport guruhi tanlanmagan</h5>
                  <p className="text-600 mb-0">Chap paneldan kerakli guruhni tanlang, so‘ng talabalar ro‘yxati va pasport formasi ochiladi.</p>
                </div>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>

      <Modal isOpen={detailModalOpen} toggle={closeModal} size="xl" className="psi-passport-modal">
        <ModalHeader toggle={closeModal}>
          {detailData.student ? detailData.student.full_name : 'Psixologik pasport'}
        </ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <div className="text-center py-6">
              <Spinner color="primary" className="mb-3" />
              <div className="text-600">Talaba pasporti yuklanmoqda...</div>
            </div>
          ) : detailData.student ? (
            <>
              <div className="psi-passport-profile mb-4">
                <div className="psi-passport-profile__info">
                  <div className="psi-passport-section mb-3">
                    <h6 className="psi-passport-section__title">Asosiy ma`lumot</h6>
                    <Row form>
                      <Col md="6">
                        <FormGroup>
                          <Label>F.I.Sh</Label>
                          <Input value={formState.full_name} disabled className="psi-passport-readonly" />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Fakultet va guruh</Label>
                          <Input value={`${formState.faculty} / ${formState.group}`} disabled className="psi-passport-readonly" />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Tug`ilgan sana</Label>
                          <Input value={formState.birth_date} disabled className="psi-passport-readonly" />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>Doimiy manzil</Label>
                          <Input value={formState.address} disabled className="psi-passport-readonly" />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="psi-passport-photo">
                  <img
                    src={imagePreview || toBackendUrl(detailData.student.image_url)}
                    alt={detailData.student.full_name}
                    className="psi-passport-photo__image"
                  />
                  <div className="psi-passport-photo__meta">
                    <Badge pill className={completionMeta(detailData.student.profile_complete).className}>
                      {completionMeta(detailData.student.profile_complete).label}
                    </Badge>
                    {permissions.manage ? (
                      <>
                        <Label for="passport-image-upload" className="psi-passport-photo__button">
                          Rasmni yangilash
                        </Label>
                        <Input
                          id="passport-image-upload"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                        <span className="psi-passport-photo__hint">Maksimal hajm: 1 MB</span>
                      </>
                    ) : (
                      <span className="psi-passport-photo__hint">Sizda faqat ko`rish huquqi bor.</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="psi-passport-section mb-4">
                <h6 className="psi-passport-section__title">Psixologik test natijalari</h6>
                <div className="psi-passport-test-grid">
                  {TEST_CARDS.map(test => (
                    <div key={test.key} className="psi-passport-test-card">
                      <div className="psi-passport-test-card__icon">
                        <FontAwesomeIcon icon={test.icon} />
                      </div>
                      <div className="psi-passport-test-card__copy">
                        <strong>{test.label}</strong>
                        <span>{detailData.tests[test.key] || 'Qiymatlanmagan'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="psi-passport-section">
                <h6 className="psi-passport-section__title">Pasport formasi</h6>
                <Row form>
                  <Col md="6">
                    <FormGroup>
                      <Label for="oila">Oilaviy ahvoli</Label>
                      <Input
                        id="oila"
                        name="oila"
                        type="select"
                        value={formState.oila}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      >
                        {OILA_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="bitirgan_nomi">Institutgacha tamomlagan o`quv yurti</Label>
                      <Input
                        id="bitirgan_nomi"
                        name="bitirgan_nomi"
                        value={formState.bitirgan_nomi}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label for="ota_ona_manzil">Oila a`zolari haqida ma`lumot</Label>
                      <Input
                        id="ota_ona_manzil"
                        name="ota_ona_manzil"
                        type="textarea"
                        rows="3"
                        value={formState.ota_ona_manzil}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="otasi_fio">Otasi F.I.Sh / telefon</Label>
                      <Input
                        id="otasi_fio"
                        name="otasi_fio"
                        value={formState.otasi_fio}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="onasi_fio">Onasi F.I.Sh / telefon</Label>
                      <Input
                        id="onasi_fio"
                        name="onasi_fio"
                        value={formState.onasi_fio}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label for="lavozim">Institutgacha ishlagan joyi, lavozimi, staji</Label>
                      <Input
                        id="lavozim"
                        name="lavozim"
                        type="textarea"
                        rows="3"
                        value={formState.lavozim}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="harbiy_qism">Harbiy xizmat ma`lumoti</Label>
                      <Input
                        id="harbiy_qism"
                        name="harbiy_qism"
                        value={formState.harbiy_qism}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="yashash_manzil">Yashash sharoiti va manzil</Label>
                      <Input
                        id="yashash_manzil"
                        name="yashash_manzil"
                        value={formState.yashash_manzil}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label>Shakl tanlovi</Label>
                      <div className="psi-passport-shape-grid">
                        {SHAPE_OPTIONS.map(option => (
                          <label
                            key={option.value}
                            className={`psi-passport-shape-option ${formState.shakl === option.value ? 'is-active' : ''} ${
                              !permissions.manage ? 'is-disabled' : ''
                            }`}
                          >
                            <input
                              type="radio"
                              name="shakl"
                              value={option.value}
                              checked={formState.shakl === option.value}
                              onChange={handleFieldChange}
                              disabled={!permissions.manage}
                            />
                            <span className="psi-passport-shape-option__glyph">
                              <img
                                src={toBackendUrl(option.asset)}
                                alt={option.label}
                                className="psi-passport-shape-option__image"
                              />
                            </span>
                            <span className="psi-passport-shape-option__label">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="xarakter">Xarakter</Label>
                      <Input
                        id="xarakter"
                        name="xarakter"
                        type="select"
                        value={formState.xarakter}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      >
                        {XARAKTER_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="rang">Yoqtirgan rang</Label>
                      <Input
                        id="rang"
                        name="rang"
                        value={formState.rang}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="salomatlik">Salomatligi haqida umumiy ma`lumot</Label>
                      <Input
                        id="salomatlik"
                        name="salomatlik"
                        type="textarea"
                        rows="3"
                        value={formState.salomatlik}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="ozlashtirish">Fanlarni o`zlashtirishi</Label>
                      <Input
                        id="ozlashtirish"
                        name="ozlashtirish"
                        type="textarea"
                        rows="3"
                        value={formState.ozlashtirish}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="munosabat">Oilaviy munosabatlar xususiyatlari</Label>
                      <Input
                        id="munosabat"
                        name="munosabat"
                        type="textarea"
                        rows="3"
                        value={formState.munosabat}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="kursdosh">Kursdoshlari bilan munosabati</Label>
                      <Input
                        id="kursdosh"
                        name="kursdosh"
                        type="textarea"
                        rows="3"
                        value={formState.kursdosh}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup className="mb-0">
                      <Label for="izoh">Mutaxassis izohi</Label>
                      <Input
                        id="izoh"
                        name="izoh"
                        type="textarea"
                        rows="6"
                        value={formState.izoh}
                        onChange={handleFieldChange}
                        disabled={!permissions.manage}
                        className="psi-passport-input"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeModal} disabled={detailSaving}>
            Yopish
          </Button>
          {permissions.manage ? (
            <Button color="primary" onClick={handleSave} disabled={detailLoading || detailSaving || !detailData.student}>
              {detailSaving ? <Spinner size="sm" className="mr-2" /> : <FontAwesomeIcon icon="save" className="mr-2" />}
              Saqlash
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PassportManagement;
